import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom'; 
import { XCircle, Download, List, Loader2, Mail } from 'lucide-react';
import { getAnalysisSummary, downloadResult, sendResultToEmail } from '../../api/BackendApi.jsx'; 
import ActionButton from '../../components/ActionButton.jsx'; 
import { useNotification } from "../../context/NotificationContext";

// This page shows the statistics/summary of a finished analysis
const AnalysisSummaryPage = () => {
    const navigate = useNavigate(); 
    const { id } = useParams();
    const [question, setQuestion] = useState('');
    const [summaryStatus, setSummaryStatus] = useState('loading'); 
    const [summaryData, setSummaryData] = useState(null);

    const { notify } = useNotification();

    // Fetch the Analysis Summary on load
    useEffect(() => {
        if (!id) {
            setSummaryStatus('error');
            setSummaryData({ message: 'ID analýzy nebylo nalezeno v URL.' });
            return;
        }

        let isMounted = true;
        let pollingTimer = null;

        const fetchSummary = async () => {
            try {
                const response = await getAnalysisSummary(id);
                
                if (!isMounted) return;

                // Check status logic
                if (response.status === 'started' || response.status === 'paid' || response.status === 'suggested' || response.status === 'authorized') {
                    setSummaryStatus('processing');
                    setSummaryData({ message: response.message || 'Zpracováváme požadavek' });
                    
                    pollingTimer = setTimeout(fetchSummary, 5000); // Poll again in 5 seconds
                } else if (response.status === 'finished') {
                    setSummaryData(response);
                    setSummaryStatus('finished');
                    setQuestion(response.question);
                    window.dataLayer.push({ event: 'step_results' });
                } else {
                    setSummaryStatus('error');
                    setSummaryData({ message: response.message || 'Analýzu se nepodařilo dokončit úspěšně.' });
                }
            } catch (error) {
                if (!isMounted) return;
                console.error("Fetch summary error:", error);
                setSummaryData({ message: error.message || 'Chyba komunikace.' });
                setSummaryStatus('error');
            }
        };

        fetchSummary();

        // Cleanup on unmount
        return () => {
            isMounted = false;
            if (pollingTimer) clearTimeout(pollingTimer);
        };
    }, [id, navigate]);


    // Handle Download
    const handleDownload = async () => {
        if (!id) {
            console.error("Chyba: ID analýzy nebylo nalezeno.");
            return;
        }

        try {
            await downloadResult(id);
            window.dataLayer.push({ event: 'results_downloaded' });
        } catch (error) {
            console.error("Download failed:", error);
        }
    };

    const handleSendToEmail = async () => {
        if (!id) {
            notify("Chyba: ID analýzy nebylo nalezeno.", "error");
            return;
        }

        try {
            const response = await sendResultToEmail(id);
            window.dataLayer.push({ event: 'results_sent_to_email' });
            notify(response.message, "success");
        } catch (error) {
            console.error("Send to email failed:", error);
            notify("Výsledek se nepodařilo odeslat.", "error");
        }
    };

    const renderSummaryContent = () => {
        // Sort by count (descending), keep all items
        const sortedCategories = summaryData?.statistics 
            ? Object.entries(summaryData.statistics).sort(([catA, countA], [catB, countB]) => {
                if (countB !== countA) {
                    return countB - countA; 
                }
                return catA.localeCompare(catB);
            })
            : [];

        return (
            <div className="w-full flex flex-col animate-fade-in"> 
                <p className="text-gray-600 mb-4">Souhrn četností a kategorií:</p>
                <div 

                    className="w-full bg-gray-50 p-4 rounded-lg border overflow-y-auto" 
                    style={{ maxHeight: '500px' }} 
                >
                    <ul className="space-y-3">
                        {sortedCategories.length > 0 ? (
                            sortedCategories.map(([category, count]) => (
                                <li key={category} className="text-gray-800 flex justify-between items-center text-base bg-white p-3 rounded-md shadow-sm">
                                    <span className="font-medium">{category}</span>
                                    <span className="ml-auto mr-2 text-gray-500">
                                        {summaryData?.answers_count ? ((count / summaryData.answers_count) * 100).toFixed(1) : "0.0"} %
                                    </span>
                                    <span className="font-bold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">{count}</span>
                                </li>
                            ))
                        ) : (
                            <li className="text-gray-500 text-center p-4">Žádné statistiky k zobrazení.</li>
                        )}
                    </ul>
                </div>
                
                {summaryData.splits && summaryData.splits.length > 0 && (
                    <div className="mt-10">
                        <p className="text-gray-600 mb-4">
                            Následující odpovědi byly rozpoznány, že obsahují více kategorií. Součet kategorií je tedy vyšší než počet odpovědí.
                        </p>
                        
                        <div 
                            className="w-full overflow-y-auto pr-2" 
                            style={{ maxHeight: '300px' }}
                        >
                            <ul className="list-disc list-inside space-y-1.5 text-gray-700 text-sm">
                                {summaryData.splits.map((splitItem, index) => (
                                    <li key={index}>
                                        {/* Keeps the safety check for arrays */}
                                        {Array.isArray(splitItem) ? splitItem.join(" | ") : splitItem}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (summaryStatus === 'loading') {
        return (
            <div className="flex justify-center min-h-full p-4 pt-8 pb-16">
                <div className="w-full max-w-4xl">
                    <div className="flex flex-col items-center justify-center h-96 text-center">
                        <div className="relative">
                            <Loader2 className="h-16 w-16 mb-6 text-blue-500 animate-spin" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">Komunikace se serverem</h3>
                    </div>
                </div>
            </div>
        );
    }

    if (summaryStatus === 'processing') {
        return (
            <div className="flex justify-center min-h-full p-4 pt-8 pb-16">
                <div className="w-full max-w-4xl">
                    <div className="flex flex-col items-center justify-center h-96 text-center">
                        <div className="relative">
                            <Loader2 className="h-16 w-16 mb-6 text-blue-500 animate-spin" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">Analýza probíhá</h3>
                        <p className="text-gray-600 max-w-md animate-pulse">
                            Probíhá kategorizace, prosím počkejte. </p>
                        <p className="text-gray-600 max-w-md animate-pulse">Tato část obvykle trvá 1-5 minut.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (summaryStatus === 'error') {
        return (
            <div className="flex justify-center min-h-full p-4 pt-8 pb-16">
                <div className="w-full max-w-4xl">
                    <div className="flex flex-col items-center justify-center h-96 text-center text-red-600">
                        <XCircle className="h-12 w-12 mb-4" />
                        <h3 className="text-xl font-bold">Tato analýza se nepovedla nebo byla odstraněna.</h3>
                        <p className="mt-2 text-gray-600 max-w-md">Registrovaným uživatelům byly kredity přičteny zpět.</p>
                        <p className="mt-2 text-gray-600 max-w-md">Neregistrovaným uživatelům se platba nestrhne.</p>
                        <p className="mt-2 text-gray-600 max-w-md">Můžete zkusit zadat analýzu znovu.</p>
                        {/*<p className="mt-2 text-gray-600 max-w-md">Chyba: {summaryData?.message}</p>*/}
                    </div>
                </div>
            </div>
        );
    }
    
    // success or success_first
    return (
        <div className="flex justify-center min-h-full p-4 pt-8 pb-16">
            <div className="w-full max-w-4xl">
                <div className="flex flex-col items-center w-full">
                    
                    {/* Header */}
                    <div className="w-full text-center mb-6">
                        <h2 className="text-3xl font-bold text-gray-800">
                            Souhrn Analýzy
                        </h2>
                        <div className="items-start gap-2 rounded-lg mb-4 text-gray-500">
                            <p>Otázka: '{question}'</p>
                        </div>
                        <p className="text-lg text-gray-600 mt-2">{summaryData?.message}</p>
                    </div>

                    {summaryData.can_rerun === true && (
                        <div className="p-5 bg-blue-50 gap-4 mb-6 pb-6 border border-blue-200 rounded-lg flex flex-col items-center text-center animate-fade-in">
                            <h3 className="text-lg font-bold text-blue-900 mb-2">
                                V analýze je možné pokračovat
                            </h3>
                            <p className="text-blue-700 mb-4 max-w-xl">
                                Prohlédněte si neurčené odpovědi, upravte kategorie, přiřaďte odpovědi a spusťte analýzu znovu nebo uložte upravený výsledek.
                            </p>
                            <ActionButton 
                                variant="solid" 
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={() => {navigate(`/analysis/create?step=4&fileId=${id}`)}}
                            >
                                Upřesnit výsledky
                            </ActionButton>
                        </div>
                    )}
                    
                    {/* Toolbar */}
                    <div className="w-full flex flex-col md:flex-row gap-4 mb-6 pb-6 border-b border-gray-200">
                        <ActionButton 
                            onClick={() => navigate(`/analysis/${id}/details`)}
                            className="w-full md:w-auto !bg-white !text-blue-600 !border !border-blue-600 hover:!bg-blue-50 shadow-none"
                            variant={'outline'}
                            icon={<List size={18} />}
                        >
                            Zobrazit detaily
                        </ActionButton>
                        
                        <div className="flex-grow"></div>
                        
                        <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                            <ActionButton 
                                onClick={handleSendToEmail}
                                className="w-full md:w-auto"
                                variant="outline"
                                icon={<Mail size={18} />}
                                title="Odešle výsledek analýzy na emailovou adresu tohoto účtu."
                            >
                                Poslat na email
                            </ActionButton>
                            <ActionButton 
                                onClick={handleDownload}
                                className="w-full md:w-auto"
                                variant="solid"
                                icon={<Download size={18} />}
                            >
                                Stáhnout výsledek
                            </ActionButton>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="w-full">
                        {renderSummaryContent()}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default AnalysisSummaryPage;

